/**
 * Die wiederkehrenden Teile der Oberfläche.
 *
 * Warum sie hier zentral stehen: Ein Backoffice, in dem jeder Bildschirm sein
 * eigenes Textfeld und seinen eigenen Fehlerkasten mitbringt, sieht nach drei
 * Bildschirmen zusammengewürfelt aus — und schlimmer: die Fehlerbehandlung
 * wird an jeder Stelle ein bisschen anders vergessen. Hier ist festgelegt,
 * dass ein Fehler immer gleich aussieht und immer einen Weg zurück anbietet.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { Klarfehler } from '../daten/fehler';
import type { Freigabefrage } from '../daten/auftraege';
import { zahlLesen, type Zahl } from '../daten/zahl';

/* ------------------------------------------------------------------ Karte */

export function Karte(eigenschaften: {
  titel?: ReactNode;
  werkzeuge?: ReactNode;
  children: ReactNode;
  /** Ohne Polsterung, wenn direkt eine Tabelle darin liegt. */
  blank?: boolean;
}) {
  return (
    <section className="karte">
      {(eigenschaften.titel || eigenschaften.werkzeuge) && (
        <header className="karte__kopf">
          <h2>{eigenschaften.titel}</h2>
          {eigenschaften.werkzeuge}
        </header>
      )}
      {eigenschaften.blank ? (
        eigenschaften.children
      ) : (
        <div className="karte__leib">{eigenschaften.children}</div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ Knopf */

type Knopfart = 'haupt' | 'zweit' | 'still' | 'gefahr';

export function Knopf(eigenschaften: {
  art?: Knopfart;
  klein?: boolean;
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  title?: string;
}) {
  const { art = 'zweit', klein, ...rest } = eigenschaften;
  return (
    <button
      type={rest.type ?? 'button'}
      className={`knopf knopf--${art}${klein ? ' knopf--klein' : ''}`}
      onClick={rest.onClick}
      disabled={rest.disabled}
      title={rest.title}
    >
      {rest.children}
    </button>
  );
}

/* ------------------------------------------------------------------- Feld */

export function Feld(eigenschaften: {
  marke: string;
  hinweis?: string;
  fehler?: string | null;
  children: ReactNode;
}) {
  return (
    <label className={`feld${eigenschaften.fehler ? ' feld--fehler' : ''}`}>
      <span className="feld__marke">{eigenschaften.marke}</span>
      {eigenschaften.children}
      {eigenschaften.fehler ? (
        <span className="feld__fehler">{eigenschaften.fehler}</span>
      ) : eigenschaften.hinweis ? (
        <span className="feld__hinweis">{eigenschaften.hinweis}</span>
      ) : null}
    </label>
  );
}

export function Textfeld(eigenschaften: {
  marke: string;
  wert: string;
  setzen: (wert: string) => void;
  hinweis?: string;
  fehler?: string | null;
  art?: string;
  platzhalter?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  autoComplete?: string;
}) {
  return (
    <Feld
      marke={eigenschaften.marke}
      hinweis={eigenschaften.hinweis}
      fehler={eigenschaften.fehler}
    >
      <input
        type={eigenschaften.art ?? 'text'}
        value={eigenschaften.wert}
        placeholder={eigenschaften.platzhalter}
        autoFocus={eigenschaften.autoFocus}
        disabled={eigenschaften.disabled}
        autoComplete={eigenschaften.autoComplete}
        onChange={(e) => eigenschaften.setzen(e.target.value)}
      />
    </Feld>
  );
}

export function Auswahlfeld<W extends string>(eigenschaften: {
  marke: string;
  wert: W;
  setzen: (wert: W) => void;
  moeglichkeiten: { wert: W; text: string }[];
  hinweis?: string;
  disabled?: boolean;
}) {
  return (
    <Feld marke={eigenschaften.marke} hinweis={eigenschaften.hinweis}>
      <select
        value={eigenschaften.wert}
        disabled={eigenschaften.disabled}
        onChange={(e) => eigenschaften.setzen(e.target.value as W)}
      >
        {eigenschaften.moeglichkeiten.map((m) => (
          <option key={m.wert} value={m.wert}>
            {m.text}
          </option>
        ))}
      </select>
    </Feld>
  );
}


/* -------------------------------------------------------------- Zahlfeld */

/**
 * Ein Zahlenfeld, das eine ungültige Eingabe NICHT als „leer" durchgehen lässt.
 *
 * Es ist ein Textfeld mit eigener, strenger Umwandlung (`daten/zahl.ts`) — die
 * Begründung, warum kein `type="number"`, steht dort. Kurz: Der Browser macht
 * aus Unsinn entweder „leer" (Preis gelöscht) oder etwas Falsches („1.250,50"
 * wird 1,2505).
 *
 * Der Aufrufer bekommt den TEXT und liest ihn selbst mit `zahlLesen` — dort,
 * wo er entscheidet, ob gespeichert werden darf. Beim Verlassen des Feldes
 * erfährt er außerdem, was der Text bedeutet (`verlassen`): nichts zu
 * speichern, wenn es ungültig ist, ist die richtige Antwort — der alte Wert
 * bleibt stehen.
 *
 * Die Meldung erscheint erst NACH dem Verlassen des Feldes. Beim Tippen ist
 * „12," eine Zahl im Werden, kein Fehler — eine Meldung, die bei jeder Eingabe
 * kurz aufblitzt, wird nicht mehr gelesen.
 */
export function Zahlfeld(eigenschaften: {
  wert: string;
  setzen: (wert: string) => void;
  /** Beim Verlassen des Feldes — mit dem, was der Text als Zahl bedeutet. */
  verlassen?: (zahl: Zahl) => void;
  disabled?: boolean;
  platzhalter?: string;
  beschriftung?: string;
  /** Kennung eines Hinweises, der das Feld beschreibt (`aria-describedby`). */
  beschriebenVon?: string;
}) {
  const [beruehrt, setBeruehrt] = useState(false);
  const kaputt = zahlLesen(eigenschaften.wert).art === 'ungueltig';

  return (
    <>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={eigenschaften.wert}
        placeholder={eigenschaften.platzhalter}
        aria-label={eigenschaften.beschriftung}
        aria-describedby={eigenschaften.beschriebenVon}
        aria-invalid={(beruehrt && kaputt) || undefined}
        disabled={eigenschaften.disabled}
        onChange={(e) => eigenschaften.setzen(e.target.value)}
        onBlur={() => {
          setBeruehrt(true);
          eigenschaften.verlassen?.(zahlLesen(eigenschaften.wert));
        }}
      />
      {beruehrt && kaputt && (
        <span className="feld__fehler" role="alert">
          Das ist keine Zahl, die ich sicher lesen kann — z. B. 1250,50 oder
          1.250,50. Gespeichert wird nichts, der bisherige Wert bleibt.
        </span>
      )}
    </>
  );
}

/* -------------------------------------------------------- Konflikthinweis */

/**
 * „Inzwischen geändert auf …" — am Feld, nicht am Seitenanfang (H1.3b, E-039).
 *
 * Jemand anderes hat ein Feld geändert, das man selbst gerade ändert. Raten,
 * wer gewinnt, wäre in beide Richtungen falsch: Gewinnt der Kollege, ist die
 * eigene Eingabe still weg; gewinnt man selbst, ist seine Änderung still weg.
 * Also steht der fremde Wert am Feld, und man entscheidet. Bis dahin ist
 * Speichern gesperrt — das macht das Formular, nicht dieser Baustein.
 *
 * Die Kennung gehört an den TEXT, nicht an den ganzen Kasten: Das Feld nennt
 * ihn per `aria-describedby` als Beschreibung, und die Beschriftung der
 * beiden Knöpfe gehört nicht dazu.
 *
 * Bewusst KEINE Live-Region: Ein Kasten, der mit Inhalt eingehängt wird, wird
 * von vielen Vorleseprogrammen nicht angesagt. Angesagt wird über eine
 * Region, die das Formular von Anfang an trägt (`.nurvorlesen`).
 */
export function Konflikthinweis(eigenschaften: {
  id: string;
  /** Der fremde Wert, so wie man ihn liest („29.08.2026", „Martin Kies"). */
  wert: string;
  uebernehmen: () => void;
  behalten: () => void;
}) {
  return (
    <div className="konflikthinweis">
      <span id={eigenschaften.id}>Inzwischen geändert auf „{eigenschaften.wert}“.</span>
      <span className="reihe" style={{ gap: 6 }}>
        <Knopf art="zweit" klein onClick={eigenschaften.uebernehmen}>
          Übernehmen
        </Knopf>
        <Knopf art="still" klein onClick={eigenschaften.behalten}>
          Meins behalten
        </Knopf>
      </span>
    </div>
  );
}

/* ------------------------------------------------------- Fahrernotiz-Zeichen */

/**
 * Das kleine Sprechblasen-Zeichen: „Der Fahrer hat dazu etwas notiert."
 *
 * Es steht dort, wo über den Preis entschieden wird — in der Abrechnung und in
 * der Liste —, damit eine Standzeit nicht erst auffällt, wenn die Rechnung
 * schon geschrieben ist. Als Zeichen und nicht als Text: In einer Tabellenzelle
 * ist kein Platz für einen Satz, und die Zeilenhöhe darf sich nicht ändern.
 *
 * Klickbar nur, wenn `umschalten` da ist (Abrechnung: klappt den Text unter
 * der Zeile auf). In der Liste bleibt es Anzeige mit dem Text als Hinweis.
 */
export function Fahrernotizzeichen(eigenschaften: {
  text: string;
  offen?: boolean;
  umschalten?: () => void;
}) {
  const bild = (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <path
        d="M2 2.5h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7l-3.5 3v-3H2a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (!eigenschaften.umschalten) {
    return (
      <span
        className="notizzeichen"
        title={`Der Fahrer hat notiert: ${eigenschaften.text}`}
        role="img"
        aria-label="Der Fahrer hat etwas notiert"
      >
        {bild}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`notizzeichen notizzeichen--knopf${
        eigenschaften.offen ? ' notizzeichen--offen' : ''
      }`}
      aria-expanded={eigenschaften.offen ?? false}
      aria-label="Notiz des Fahrers anzeigen"
      title="Der Fahrer hat etwas notiert — anklicken zum Lesen"
      onClick={(e) => {
        e.stopPropagation();
        eigenschaften.umschalten?.();
      }}
    >
      {bild}
    </button>
  );
}

/**
 * Was die Fahrer notiert haben, als abgesetzter Block — nur lesen.
 *
 * Eine Zeile je Notiz („Name: Text"), wie die Datenbank sie liefert. Das Büro
 * schreibt nie in die Spalte des Fahrers; dieser Block hat deshalb kein Feld.
 */
export function Fahrernotizblock(eigenschaften: {
  titel?: string;
  zeilen: string[];
}) {
  return (
    <div className="fahrernotiz" data-fahrernotiz>
      <div className="fahrernotiz__titel">
        {eigenschaften.titel ?? 'Notiz des Fahrers'}
      </div>
      {eigenschaften.zeilen.map((zeile, nr) => (
        <p key={`${nr}:${zeile}`} className="fahrernotiz__text">
          {zeile}
        </p>
      ))}
    </div>
  );
}


/**
 * Der Inhalt der Rückfrage vor einer Freigabe: was fehlt, und was der Fahrer
 * notiert hat.
 *
 * Es gibt sie an zwei Stellen (Liste und Detailsicht), und beide sollen
 * dasselbe sagen — deshalb steht sie einmal hier. Zwei Abschnitte, nur einer
 * davon, oder beide: „Der Fahrer hat notiert:" bekommt einen eigenen Absatz,
 * damit eine Standzeit nicht zwischen den Mängeln einer Liste untergeht.
 */
export function Freigabeinhalt(eigenschaften: { frage: Freigabefrage }) {
  const { fehlt, notiz } = eigenschaften.frage;
  return (
    <>
      {fehlt.length > 0 && (
        <ul className="fehltliste">
          {fehlt.map((satz) => (
            <li key={satz}>{satz}</li>
          ))}
        </ul>
      )}
      {notiz && (
        <div className="fahrernotiz" data-fahrernotiz style={{ marginBottom: 10 }}>
          <div className="fahrernotiz__titel">Der Fahrer hat notiert:</div>
          <p className="fahrernotiz__text">{notiz}</p>
        </div>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- Meldung */

export function Meldung(eigenschaften: {
  art: 'fehler' | 'warnung' | 'gut' | 'neutral';
  titel?: string;
  children: ReactNode;
  knopf?: { text: string; tun: () => void };
}) {
  return (
    <div className={`meldung meldung--${eigenschaften.art}`} role="status">
      <div>
        {eigenschaften.titel && (
          <strong className="meldung__titel">{eigenschaften.titel}</strong>
        )}
        {eigenschaften.children}
      </div>
      {eigenschaften.knopf && (
        <button className="meldung__knopf" onClick={eigenschaften.knopf.tun}>
          {eigenschaften.knopf.text}
        </button>
      )}
    </div>
  );
}

/**
 * Ein Fehler aus der Datenbank, aufbereitet.
 *
 * „Nochmal versuchen" steht bewusst nicht immer da: Bei fehlender
 * Berechtigung hilft Wiederholen nicht, und ein Knopf, der nichts bewirkt,
 * kostet Vertrauen. Angeboten wird er nur, wo er auch sinnvoll ist.
 */
export function Fehlerkasten(eigenschaften: {
  fehler: Klarfehler;
  nochmal?: () => void;
}) {
  const lohntWiederholung =
    eigenschaften.fehler.art === 'netz' ||
    eigenschaften.fehler.art === 'unbekannt' ||
    eigenschaften.fehler.art === 'gleichzeitig_geaendert';

  return (
    <Meldung
      art="fehler"
      titel={eigenschaften.fehler.titel}
      knopf={
        eigenschaften.nochmal && lohntWiederholung
          ? { text: 'Nochmal versuchen', tun: eigenschaften.nochmal }
          : undefined
      }
    >
      {eigenschaften.fehler.text}
    </Meldung>
  );
}

/* --------------------------------------------------------------- Rückfrage */

/**
 * Eine Frage, die man beantworten muss, bevor etwas passiert.
 *
 * ═══ WANN DAS ANGEBRACHT IST UND WANN NICHT ═══
 *
 * Nur für Schritte, die etwas festschreiben, was jemand anderes später glaubt —
 * eine Tour zur Abrechnung freigeben, obwohl ein Beleg fehlt. Nicht für jeden
 * Speichervorgang: Eine Rückfrage, die immer kommt, wird immer weggeklickt und
 * schützt dann vor nichts mehr.
 *
 * Der bestätigende Knopf trägt, was er tut („Trotzdem freigeben"), nicht „OK".
 * Wer die Frage überflogen hat, liest im Zweifel nur den Knopf.
 */
export function Rueckfrage(eigenschaften: {
  titel: string;
  children: ReactNode;
  bestaetigungstext: string;
  art?: 'haupt' | 'gefahr';
  laeuft?: boolean;
  /**
   * Verlangt die Rückfrage noch eine Eingabe (etwa den Stornogrund), dann ist
   * der bestätigende Knopf gesperrt, bis sie da ist. Getrennt von `laeuft`,
   * weil das zwei verschiedene Gründe sind — und weil ein Knopf, der aus
   * ungenanntem Grund grau ist, wie ein Fehler aussieht.
   */
  bestaetigungGesperrt?: boolean;
  abbrechen: () => void;
  bestaetigen: () => void;
}) {
  // ═══ DER FOKUS GEHT IN DIE FRAGE — UND DANACH ZURÜCK (Review HJ-026) ═══
  //
  // Wer mit der Tastatur arbeitet, drückte Enter auf einen Knopf, und die
  // Frage erschien irgendwo — ohne dass ein Vorleseprogramm sie ansagte, und
  // der Fokus blieb am Knopf oder fiel an den Seitenanfang. Jetzt: beim
  // Öffnen in die Frage (erstes Eingabefeld, sonst „Abbrechen" — der sichere
  // Knopf, nicht der folgenreiche), Escape bricht ab, und beim Schließen geht
  // der Fokus dorthin zurück, woher er kam.
  const titelId = useId();
  const textId = useId();
  const rahmen = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const vorher = document.activeElement as HTMLElement | null;
    const ziel =
      rahmen.current?.querySelector<HTMLElement>('input, textarea, select') ??
      rahmen.current?.querySelector<HTMLElement>('[data-abbrechen]');
    ziel?.focus();
    return () => {
      if (vorher && vorher.isConnected) vorher.focus();
    };
  }, []);

  return (
    <div
      ref={rahmen}
      className="rueckfrage"
      role="alertdialog"
      aria-labelledby={titelId}
      aria-describedby={textId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          eigenschaften.abbrechen();
        }
      }}
    >
      <strong className="rueckfrage__titel" id={titelId}>
        {eigenschaften.titel}
      </strong>
      <div className="rueckfrage__text" id={textId}>
        {eigenschaften.children}
      </div>
      <div className="reihe reihe--rechts">
        <button
          type="button"
          data-abbrechen
          className="knopf knopf--still"
          onClick={eigenschaften.abbrechen}
        >
          Abbrechen
        </button>
        <Knopf
          art={eigenschaften.art ?? 'haupt'}
          disabled={eigenschaften.laeuft || eigenschaften.bestaetigungGesperrt}
          onClick={eigenschaften.bestaetigen}
        >
          {eigenschaften.bestaetigungstext}
        </Knopf>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Marker */

export function Marker(eigenschaften: {
  /**
   * `gut_voll` ist das „entschieden" neben dem „möglich" von `gut` (HJ-031):
   * gefüllt und mit Haken, damit sich beide nicht bloß im Farbton
   * unterscheiden.
   */
  art: 'gut' | 'gut_voll' | 'warn' | 'schlecht' | 'neutral';
  children: ReactNode;
  /** Für den einen Zustand, der von der Tür aus lesbar sein muss. */
  gross?: boolean;
}) {
  return (
    <span
      className={`marker marker--${eigenschaften.art.replace('_', '-')}${
        eigenschaften.gross ? ' marker--gross' : ''
      }`}
    >
      {eigenschaften.art === 'gut_voll' && <span aria-hidden="true">✓ </span>}
      {eigenschaften.children}
    </span>
  );
}

/* ----------------------------------------------------------------- Laden */

export function Ladehinweis(eigenschaften: { was?: string }) {
  return <div className="laden">{eigenschaften.was ?? 'Wird geladen …'}</div>;
}

/**
 * Suchfeld über einer Liste.
 *
 * Bewusst ohne Knopf und ohne Verzögerung: Es wird lokal gefiltert, weil bei
 * dreistelligen Listen jede Serverrunde nur Wartezeit wäre. Getippt wird
 * fehlertolerant gesucht (siehe daten/textvergleich.ts) — „hersch" muss „Werk
 * Herrschaftsholz" finden, sonst legt jemand einen zweiten an.
 */
export function Suchleiste(eigenschaften: {
  wert: string;
  setzen: (wert: string) => void;
  platzhalter: string;
  anzahl?: string;
}) {
  return (
    <div className="reihe" style={{ gap: 12, flex: 1 }}>
      <input
        type="search"
        className="suchfeld"
        value={eigenschaften.wert}
        placeholder={eigenschaften.platzhalter}
        onChange={(e) => eigenschaften.setzen(e.target.value)}
      />
      {eigenschaften.anzahl && <span className="meta">{eigenschaften.anzahl}</span>}
    </div>
  );
}

/**
 * Tabelle mit den drei ehrlichen Zuständen an einer Stelle.
 *
 * Warum das zentral steht: Ohne diesen Baustein bekommt jeder Bildschirm sein
 * eigenes „lädt", sein eigenes „leer" und — schlimmer — seine eigene Art, den
 * Fehlerfall zu vergessen. Hier ist festgelegt, dass eine Liste, die nicht
 * geladen werden konnte, das SAGT und einen Weg zurück anbietet.
 */
export function Tabellenkarte<T>(eigenschaften: {
  titel: ReactNode;
  werkzeuge?: ReactNode;
  laedt: boolean;
  fehler: Klarfehler | null;
  nochmal: () => void;
  eintraege: T[] | null;
  spalten: { text: string; rechts?: boolean }[];
  leer: ReactNode;
  zeile: (eintrag: T) => ReactNode;
}) {
  return (
    <Karte titel={eigenschaften.titel} werkzeuge={eigenschaften.werkzeuge} blank>
      {eigenschaften.laedt && !eigenschaften.eintraege ? (
        <Ladehinweis />
      ) : eigenschaften.fehler ? (
        <div style={{ padding: 16 }}>
          <Fehlerkasten
            fehler={eigenschaften.fehler}
            nochmal={eigenschaften.nochmal}
          />
        </div>
      ) : (
        <table className="tabelle">
          <thead>
            <tr>
              {eigenschaften.spalten.map((s) => (
                <th key={s.text} className={s.rechts ? 'tabelle__rechts' : undefined}>
                  {s.text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(eigenschaften.eintraege ?? []).length === 0 ? (
              <LeereListe spalten={eigenschaften.spalten.length}>
                {eigenschaften.leer}
              </LeereListe>
            ) : (
              (eigenschaften.eintraege ?? []).map(eigenschaften.zeile)
            )}
          </tbody>
        </table>
      )}
    </Karte>
  );
}

export function LeereListe(eigenschaften: {
  children: ReactNode;
  spalten: number;
}) {
  return (
    <tr>
      <td className="tabelle__leer" colSpan={eigenschaften.spalten}>
        {eigenschaften.children}
      </td>
    </tr>
  );
}

/* ------------------------------------------------------ Bestätigungsdialog */

/**
 * Ein echtes Fenster über der Seite — für Schritte, die sich NICHT
 * zurücknehmen lassen (HJ-032: eine Datei endgültig löschen).
 *
 * Warum nicht die `Rueckfrage` in der Seite: Die steht neben dem Knopf und
 * lässt alles andere bedienbar — richtig für „Trotzdem freigeben", wo man
 * noch etwas nachsehen will. Beim Löschen soll nichts anderes erreichbar
 * sein, bis entschieden ist. Das native `<dialog>` mit `showModal()` sperrt
 * den Rest der Seite, hält den Fokus im Fenster und schließt mit Escape —
 * ohne Bibliothek und ohne selbstgebaute Fokusfalle.
 *
 * Der Fokus steht beim Öffnen auf „Abbrechen", nicht auf dem folgenreichen
 * Knopf: Wer mit Enter durch die Seite geht, löscht nicht aus Versehen.
 */
export function Bestaetigungsdialog(eigenschaften: {
  titel: string;
  children: ReactNode;
  bestaetigungstext: string;
  laeuft?: boolean;
  abbrechen: () => void;
  bestaetigen: () => void;
}) {
  const fenster = useRef<HTMLDialogElement>(null);
  const abbrechenKnopf = useRef<HTMLButtonElement>(null);
  const titelId = useId();
  const textId = useId();

  useEffect(() => {
    const dialog = fenster.current;
    if (!dialog) return;
    const vorher = document.activeElement as HTMLElement | null;
    if (!dialog.open) dialog.showModal();
    abbrechenKnopf.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      if (vorher && vorher.isConnected) vorher.focus();
    };
  }, []);

  return (
    <dialog
      ref={fenster}
      className="bestaetigung"
      aria-labelledby={titelId}
      aria-describedby={textId}
      // Escape löst `cancel` aus. Abgefangen, damit React entscheidet, wann
      // das Fenster zugeht — sonst schlösse der Browser es selbst, und der
      // Zustand der Seite hielte es weiter für offen.
      onCancel={(e) => {
        e.preventDefault();
        eigenschaften.abbrechen();
      }}
    >
      <strong className="rueckfrage__titel" id={titelId}>
        {eigenschaften.titel}
      </strong>
      <div className="rueckfrage__text" id={textId}>
        {eigenschaften.children}
      </div>
      <div className="reihe reihe--rechts">
        <button
          ref={abbrechenKnopf}
          type="button"
          className="knopf knopf--still"
          onClick={eigenschaften.abbrechen}
        >
          Abbrechen
        </button>
        <Knopf
          art="gefahr"
          disabled={eigenschaften.laeuft}
          onClick={eigenschaften.bestaetigen}
        >
          {eigenschaften.bestaetigungstext}
        </Knopf>
      </div>
    </dialog>
  );
}
